
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type ReferralRow = Database['public']['Tables']['referrals']['Row'];

interface ReferralStats {
  totalReferrals: string;
  successfulSignups: string;
  premiumConversions: string;
  rewardsEarned: string;
  progressToNextReward: number;
  recentReferrals: {
    date: string;
    email: string;
    status: string;
  }[];
}

export function useReferralStats(userId?: string) {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const REWARD_THRESHOLD = 3; // Number of conversions needed for each reward

  useEffect(() => {
    const fetchReferralStats = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setStatusMessage(null);
        setIsFromCache(false);

        // Check if offline and use offline service
        if (!navigator.onLine) {
          try {
            const { offlineReferralService } = await import('@/services/referral/offlineReferralService');
            const isAvailable = await offlineReferralService.isReferralDataAvailable(userId);

            if (isAvailable) {
              const offlineStats = await offlineReferralService.getReferralStats(userId);
              setStats(offlineStats);
              setStatusMessage(offlineReferralService.getOfflineStatusMessage());
              setIsFromCache(true);
            } else {
              // Provide fallback stats for offline mode
              setStats({
                totalReferrals: '0',
                successfulSignups: '0',
                premiumConversions: '0',
                rewardsEarned: '0',
                progressToNextReward: 0,
                recentReferrals: []
              });
              setStatusMessage('Referral statistics require an internet connection for first-time setup');
            }
          } catch (offlineError) {
            // Fallback to localStorage cache
            const cachedStats = localStorage.getItem(`referral_stats_${userId}`);
            if (cachedStats) {
              setStats(JSON.parse(cachedStats));
              setStatusMessage('Referral statistics loaded from cache. Connect to internet for real-time data.');
              setIsFromCache(true);
            } else {
              setStats({
                totalReferrals: '0',
                successfulSignups: '0',
                premiumConversions: '0',
                rewardsEarned: '0',
                progressToNextReward: 0,
                recentReferrals: []
              });
              setStatusMessage('Referral statistics require an internet connection');
            }
          }
          setLoading(false);
          return;
        }

        // Get total referrals count
        const { count: totalCount, error: countError } = await supabase
          .from('referrals')
          .select('*', { count: 'exact', head: true })
          .eq('referrer_id', userId);

        if (countError) throw countError;

        // Get successful signups count (signed_up status)
        const { count: signupsCount, error: signupsError } = await supabase
          .from('referrals')
          .select('*', { count: 'exact', head: true })
          .eq('referrer_id', userId)
          .eq('status', 'signed_up');

        if (signupsError) throw signupsError;

        // Get premium conversions count (converted status)
        const { count: conversionsCount, error: conversionsError } = await supabase
          .from('referrals')
          .select('*', { count: 'exact', head: true })
          .eq('referrer_id', userId)
          .eq('status', 'converted');

        if (conversionsError) throw conversionsError;
        
        // Calculate rewards earned and progress toward next reward
        const totalConversions = conversionsCount || 0;
        const rewardsEarned = Math.floor(totalConversions / REWARD_THRESHOLD);
        const progressToNextReward = totalConversions % REWARD_THRESHOLD;

        // Get recent referrals - fixing the join query for users table
        const { data: recentReferrals, error: referralsError } = await supabase
          .from('referrals')
          .select(`
            created_at,
            status,
            referred_user_id
          `)
          .eq('referrer_id', userId)
          .order('created_at', { ascending: false })
          .limit(5);

        if (referralsError) throw referralsError;

        // If we have referrals, fetch the emails for those users separately
        let formattedReferrals: ReferralStats['recentReferrals'] = [];
        if (recentReferrals && recentReferrals.length > 0) {
          // Get user emails for referred users
          const referredUserIds = recentReferrals.map((ref: ReferralRow) => ref.referred_user_id);
          const { data: userData, error: userError } = await supabase
            .from('profiles')
            .select('id, username, full_name')
            .in('id', referredUserIds);

          if (userError) throw userError;

          // Format the recent referrals data
          formattedReferrals = recentReferrals.map((referral: ReferralRow) => {
            const userInfo = userData?.find((user: { id: string; username?: string; full_name?: string }) => user.id === referral.referred_user_id);
            return {
              date: referral.created_at,
              email: userInfo?.username || userInfo?.full_name || 'Anonymous User',
              status: referral.status
            };
          });
        }

        const statsData = {
          totalReferrals: totalCount?.toString() || '0',
          successfulSignups: signupsCount?.toString() || '0',
          premiumConversions: conversionsCount?.toString() || '0',
          rewardsEarned: rewardsEarned.toString(),
          progressToNextReward,
          recentReferrals: formattedReferrals
        };

        setStats(statsData);

        // Cache the stats for offline use in both localStorage and IndexedDB
        localStorage.setItem(`referral_stats_${userId}`, JSON.stringify(statsData));

        // Also cache in IndexedDB for proper offline service integration
        try {
          const { referralStatsOps } = await import('@/services/offline/indexeddb/operations');
          await referralStatsOps.cacheStats(userId, {
            total_referrals: statsData.totalReferrals,
            successful_signups: statsData.successfulSignups,
            premium_conversions: statsData.premiumConversions,
            rewards_earned: statsData.rewardsEarned,
            progress_to_next_reward: statsData.progressToNextReward,
            recent_referrals: statsData.recentReferrals.map(r => ({
              id: crypto.randomUUID(),
              created_at: r.date,
              status: r.status
            }))
          }, 24 * 60 * 60 * 1000); // Cache for 24 hours

        } catch (cacheError) {
          console.warn('Failed to cache referral stats in IndexedDB:', cacheError);
        }
      } catch (err: unknown) {
        console.error('Error fetching referral stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to load referral statistics');
        // Provide fallback stats to prevent UI errors
        setStats({
          totalReferrals: '0',
          successfulSignups: '0',
          premiumConversions: '0',
          rewardsEarned: '0',
          progressToNextReward: 0,
          recentReferrals: []
        });
      } finally {
        setLoading(false);
      }
    };

    fetchReferralStats();
  }, [userId]);

  return {
    stats,
    loading,
    error,
    statusMessage,
    isFromCache
  };
}
